<template>
  <div class="tabs" :class="[`tabs--${display}`, `tabs--${size}`]">
    <div class="tabs__headers" :class="`tabs__headers--${display}`" ref="tabHeadersRef">
      <!-- Pill indicator for pill display -->
      <div v-if="display === 'pill'" class="tabs__indicator" :style="pillIndicatorStyle"></div>

      <div
        v-for="tab in tabs"
        :key="tab.name"
        :class="[
          'tabs__header',
          `tabs__header--${display}`,
          { 'tabs__header--active': tab.name === activeTab },
        ]"
        @click="selectTab(tab.name)"
        :ref="(el) => setTabRef(el, tab.name)"
      >
        <BaseIcon v-if="tab.icon" :name="tab.icon" :size="iconSize" class="tabs__icon" />
        <span>{{ tab.title }}</span>
        <span v-if="tab.badge !== undefined && tab.badge !== null" class="tabs__badge">
          {{ tab.badge }}
        </span>
      </div>
    </div>
    <div class="tabs__content">
      <slot />
    </div>
  </div>
</template>

<script>
  import { ref, provide, watch, computed, nextTick, onMounted } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'TabsPanel',
    components: {
      BaseIcon,
    },
    props: {
      modelValue: {
        type: String,
        required: true,
      },
      display: {
        type: String,
        default: 'pill',
        validator: (value) => ['default', 'pill'].includes(value),
      },
      size: {
        type: String,
        default: 'md',
        validator: (value) => ['sm', 'md', 'lg'].includes(value),
      },
    },
    setup(props, { emit }) {
      const activeTab = ref(props.modelValue);
      const tabs = ref([]);
      const tabHeadersRef = ref(null);
      const tabRefs = ref({});
      const pillIndicatorStyle = ref({});

      // Icon size based on tab size
      const iconSize = computed(() => {
        const sizes = { sm: 14, md: 16, lg: 18 };
        return sizes[props.size] || 16;
      });

      const selectTab = (tabName) => {
        activeTab.value = tabName;
        emit('update:modelValue', tabName);

        if (props.display === 'pill') {
          nextTick(() => {
            updatePillIndicator();
          });
        }
      };

      const addTab = (tab) => {
        tabs.value.push(tab);

        if (props.display === 'pill') {
          nextTick(() => {
            updatePillIndicator();
          });
        }
      };

      const updateTab = (tabName, updates) => {
        const tabIndex = tabs.value.findIndex((t) => t.name === tabName);
        if (tabIndex !== -1) {
          tabs.value[tabIndex] = { ...tabs.value[tabIndex], ...updates };
        }
      };

      const removeTab = (tabName) => {
        tabs.value = tabs.value.filter((t) => t.name !== tabName);
        delete tabRefs.value[tabName];
      };

      const setTabRef = (el, tabName) => {
        if (el) {
          tabRefs.value[tabName] = el;
        }
      };

      const updatePillIndicator = () => {
        if (props.display !== 'pill' || !activeTab.value || !tabRefs.value[activeTab.value]) {
          return;
        }

        const activeTabEl = tabRefs.value[activeTab.value];
        const rect = activeTabEl.getBoundingClientRect();
        const containerRect = tabHeadersRef.value.getBoundingClientRect();

        // getBoundingClientRect is viewport-relative, but the indicator is
        // absolutely positioned inside the (horizontally scrollable) headers
        // and scrolls with their content — so add scrollLeft back in, otherwise
        // the pill lands on the wrong tab once the strip has been scrolled.
        pillIndicatorStyle.value = {
          width: `${rect.width}px`,
          transform: `translateX(${
            rect.left - containerRect.left + tabHeadersRef.value.scrollLeft
          }px)`,
        };
      };

      provide('addTab', addTab);
      provide('updateTab', updateTab);
      provide('removeTab', removeTab);
      provide('activeTab', activeTab);

      watch(
        () => props.modelValue,
        (newVal) => {
          activeTab.value = newVal;

          if (props.display === 'pill') {
            nextTick(() => {
              updatePillIndicator();
            });
          }
        }
      );

      onMounted(() => {
        if (props.display === 'pill') {
          nextTick(() => {
            updatePillIndicator();
          });
        }
      });

      return {
        activeTab,
        tabs,
        tabHeadersRef,
        pillIndicatorStyle,
        iconSize,
        selectTab,
        setTabRef,
        updatePillIndicator,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .tabs {
    display: flex;
    flex-direction: column;

    // Size modifiers
    &--sm {
      .tabs__header {
        padding: 0.375rem 0.75rem;
        @include fontSize('xs');
      }

      .tabs__badge {
        min-width: 16px;
        height: 16px;
        @include fontSize('xs');
      }
    }

    &--md {
      .tabs__header {
        padding: 0.5rem 1rem;
        @include fontSize('sm');
      }
    }

    &--lg {
      .tabs__header {
        padding: 0.625rem 1.25rem;
        @include fontSize('base');
      }

      .tabs__badge {
        min-width: 22px;
        height: 22px;
      }
    }

    // Default modifier
    &--default {
      .tabs__headers {
        border-bottom: 1px solid var(--slate-200);
      }

      .tabs__header {
        cursor: pointer;
        font-weight: 500;
        color: var(--slate-600);
        border-bottom: 2px solid transparent;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        &:hover {
          color: var(--slate-800);
        }

        &--active {
          color: var(--brand-600);
          border-bottom-color: var(--brand-500);
        }
      }
    }

    // Pill modifier
    &--pill {
      .tabs__headers {
        background: var(--slate-100);
        border-radius: 10px;
        padding: 4px;
        position: relative;
        display: inline-flex;
        width: auto;
        align-self: flex-start;
      }

      .tabs__header {
        cursor: pointer;
        font-weight: 500;
        color: var(--slate-600);
        border-radius: 8px;
        transition: all 0.2s ease;
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        &:hover {
          color: var(--slate-800);
        }

        &--active {
          color: var(--brand-700);
        }
      }
    }

    // Elements
    &__headers {
      display: flex;
      // Allow the header strip to scroll within itself instead of pushing the
      // page wider than the viewport when tabs overflow (e.g. on mobile).
      max-width: 100%;
      overflow-x: auto;
      scrollbar-width: none; // Firefox

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__header {
      user-select: none;
      // Keep tabs on one line and let the strip scroll (see &__headers)
      // instead of the items shrinking/wrapping when they overflow.
      flex-shrink: 0;
      white-space: nowrap;
    }

    &__icon {
      flex-shrink: 0;
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      background: var(--brand-100);
      color: var(--brand-700);
      border-radius: 10px;
      @include fontSize('xs');
      font-weight: 600;
    }

    &__indicator {
      position: absolute;
      top: 4px;
      bottom: 4px;
      left: 2px;
      background: white;
      border-radius: 8px;
      box-shadow:
        0 1px 3px 0 rgba(0, 0, 0, 0.1),
        0 1px 2px 0 rgba(0, 0, 0, 0.06);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 1;
    }

    &__content {
      padding: 1rem 0;
    }
  }
</style>
